import type { LucideIcon } from 'lucide-react'
import type { SerializedNode } from './serialized-strategy'
import type { ParametersRecord } from './parameter-value'
import type { EngineErrorCode } from '@/features/backtest/graph-engine/types'
import type { Lowering } from '@/features/backtest/utils/graph-lowering'

// Universal Node Schema - Single definition for any node type
/** See `UniversalNodeSchema.compound`. */
export interface CompoundDeclaration {
  /**
   * 'editor-only': never sent to the engine (the Report, the Universe).
   * 'lowered': rewritten by `lower` into nodes the engine knows before any run
   * (the Trade Manager into the Advanced Trade cluster).
   */
  engine?: 'native' | 'editor-only' | 'lowered'
  /** For 'lowered': the pass that rewrites every node of this type (utils/engine-view.ts). */
  lower?: Lowering
  /** Types whose lowering must run before this one's. */
  runsAfter?: readonly string[]
  /** Places trades: a graph with none of these has nothing to run. */
  placesTrades?: boolean
  /** One per strategy: a second is a readiness error with this code. */
  singleton?: { code: EngineErrorCode; noun: string; plural: string }
  /** Left out of a catalog when false: the AI's node catalog, EdgeScript's. */
  catalog?: { ai?: boolean; edgescript?: boolean }
  /** Settings that say how a run is launched, kept out of the execution signature. */
  runSettings?: readonly string[]
}

export interface UniversalNodeSchema {
  // Basic node identity
  type: string
  displayName: string
  category:
    | 'data'
    | 'indicators'
    | 'logic'
    | 'math'
    | 'entry-exit-logic'
    | 'utilities'
    | 'debug'
    | 'filter'
    | 'presets'
  icon: LucideIcon
  description?: string

  /**
   * Search aliases for the node palette. Lets users find a node by its
   * common acronym (e.g. "SMA" for Simple Moving Average) even though the
   * displayName, type, and description don't contain that substring. Each
   * alias is matched case-insensitively as a substring. Listed in order of
   * how likely a user is to type them.
   */
  aliases?: string[]

  /**
   * Schema version. Bump when parameters/inputs/outputs change shape.
   * Defaults to 1 if omitted. Pair with `migrations` to handle older serialized nodes.
   */
  version?: number

  /**
   * Forward-only migration chain keyed by source version.
   * `migrations[N]` transforms a serialized node at v{N} → v{N+1}.
   */
  migrations?: Record<number, (node: SerializedNode) => SerializedNode>

  /**
   * If true, schema is kept registered for read-only deserialization but hidden from the UI.
   * New nodes of this type cannot be created.
   */
  deprecated?: boolean

  /**
   * False: not offered where nodes are added (the palette, the command bar),
   * though still registered, so saved strategies load it and EdgeScript can
   * name it. The Trade Manager retired Simple Trade, Advanced Trade and the
   * cluster pieces that only wire into it (Chris, 2026-10-06).
   */
  addable?: boolean

  /**
   * What this node is beyond its face, declared once (compound audit step 3,
   * 2026-10-06). schemas/compound.ts derives the trade placers, the
   * editor-only types, the catalogs' exclusions, the one-per-strategy checks
   * and the run settings from these, where five files kept their own lists.
   */
  compound?: CompoundDeclaration

  /**
   * Features shared across node types, each with its own lowering after the
   * compounds' (utils/engine-view.ts). `entryWindow`: the node carries an
   * entry window (its parameters and Window socket), lowered into a Time
   * Signal and a Merge on the wire into `trigger`, the socket it hears its
   * trigger on.
   */
  facets?: { entryWindow?: { trigger: string } }

  // Preset-specific fields
  presetId?: string // ID of the preset (e.g., 'premarket-high')
  baseType?: string // Base type for presets (e.g., 'timeSlice')
  // The settings a preset fixes (hidden from `parameters`); a node built from a recipe
  // step of the preset type gets them (graph-engine/materialize.ts)
  presetParameters?: Record<string, string | number | boolean>

  // Input definitions - what this node accepts
  inputs: InputDefinition[]

  // Output definitions - what this node produces
  outputs: OutputDefinition[]

  // Parameter definitions - what users can configure
  parameters: ParameterDefinition[]

  // Input handler configuration - how inputs are managed
  // This determines requirement patterns automatically:
  // - 'single': The input is always required
  // - 'dual': Both inputs are always required
  // - 'multi': First minInputs are required, rest are optional
  // - 'all': Every declared input is required (Position in Range's value, low
  //   and high)
  inputHandler?: {
    type: 'single' | 'dual' | 'multi' | 'all'
    maxInputs?: number // Only for multi-input nodes (default: 8)
    minInputs?: number // Only for multi-input nodes (default: 2)
    /**
     * Multi-input nodes with declared, numbered inputs (Switcher, Multi Target):
     * show the filled inputs plus one empty one, never fewer than `minInputs`.
     * Without it every declared input is drawn (Advanced Trade's named
     * sockets). Nodes with no declared inputs (Merge, Sum) always grow this way.
     */
    reveal?: 'progressive'
  }

  // Equation display configuration
  /**
   * A node that reads a check or a count (logic/utilities nodes): a collapsed
   * row wired from a "{...} Count" source reads just "Counter". A plain
   * Parameters block would also draw `face.equation` under its fields; none
   * does, since only the math nodes keep their formula on the face
   * (`ui.showEquation`, Chris 2026-10-28).
   */
  hasEquation?: boolean

  /**
   * How the node draws, where it differs from its category's default
   * (schemas/node-face.ts). Kept apart from inputs, parameters and outputs,
   * which the AI node catalog and EdgeScript read; neither reads this.
   */
  face?: Partial<NodeFace>

  // Unique business logic (only thing that varies per node)

  calculate?: (
    inputs: Record<string, any>,
    parameters?: Record<string, any>,
  ) => Record<string, unknown>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validate?: (inputs: Record<string, any>, parameters?: Record<string, any>) => ValidationResult

  // UI customization (optional)
  ui?: {
    /** Whether to show a monospace formula in the outputs area and hide the first output handle (used by math nodes) */
    showEquation?: boolean
    customSections?: CustomSection[]
  }
}

/** What a node knows when it says what one of its wires carries. */
export interface SummaryContext {
  /** The node's settings. */
  params: ParametersRecord
  /** What is wired into one of this node's inputs, as that wire says it ('No Input' when nothing is). */
  input: (handle: string) => string
  /** A value typed into one of this node's inputs (a Condition's static B). */
  staticValue: (handle: string) => unknown
  /** How many wires come into this node. */
  wiredInputs: number
  /** The field the receiving input picked from an OHLCV feed: open, high, low, close or volume. */
  consumerField: string
}

/**
 * How a node draws, as opposed to what it computes. Every node takes its
 * category's face (CATEGORY_FACES in schemas/node-face.ts) and overrides only
 * what differs, so spacing, titles and rows stay the same everywhere.
 */
export interface NodeFace {
  /**
   * 280px; 305px for a sectioned face whose paired rows need a little more
   * (the Trade Manager); 330px for a node whose rows carry a toggle or long
   * pills (NODE_WIDTHS).
   */
  width: 'standard' | 'medium' | 'wide'
  /** What labels an input row: its name, a letter for an unnamed input (A, B), or nothing. */
  inputLabels: 'name' | 'letter' | 'none'
  /** One row and handle per output, or one handle with the fields listed under it (indicators). */
  outputs: 'per-output' | 'single-handle'
  /**
   * The right side of an input row, for inputs that do not set their own
   * `control`: a field picker (or a "—" box while unwired, unless the input
   * only takes true/false), an on/off toggle (Merge), nothing (the trade
   * nodes, whose sockets each take one whole thing), the values a run
   * captured with their chart rulers (Debug Values), or the plotted series'
   * colour (Debug Chart).
   */
  inputControl: 'field' | 'toggle' | 'none' | 'captured' | 'swatch' | 'source'
  /**
   * What an unwired input says. By default a required one reads "No input".
   * 'prompt' says what to connect, the input's `emptyText` or "Connect a
   * source…" (math: Chris, 2026-10-06). Either way a required one is red, the
   * red dashed line saying something needs doing; an optional one is quiet.
   */
  emptyInputs?: 'prompt'
  /** Header fill, for the one node that has its own colour. */
  headerColor?: string
  /** A node whose body is its own component rather than rows. */
  renderer?: 'switcher' | 'debugValues'
  /**
   * One "Show on candles" switch in place of the `placement` and
   * `focusOnPrice` rows, for a condition or a line by what is wired in (the
   * Debug Chart: utils/debug-show-on.ts). The two stay stored and settable by
   * name.
   */
  showOn?: boolean
  /**
   * A saved eye in the node's action bar, stored as its `visible` setting: off
   * keeps the node in the strategy but draws nothing on the chart, and the node
   * dims (the Debug Chart: utils/debug-show-on.ts readChartVisible).
   */
  chartEye?: boolean
  /**
   * A setting change that takes a socket out of play also removes its wire,
   * so the socket stops showing (the Trade Manager: Limit back to Market,
   * Custom back to PnL %). Off by default: a loaded graph keeps a wire into an
   * inactive socket visible, which many recipes rely on.
   */
  releaseInactive?: boolean
  /** The name where space is tight ("EMA", "OHLCV-1m"), and in exposed-setting aliases. */
  shortName?: string
  /** The node named with its settings ("EMA(20)"), as pills and form operands write it. Defaults to `shortName`. */
  name?: (params: ParametersRecord) => string
  /** What a wire from this node says on the input it feeds ("Close-5m > SMA(9)"). Defaults to `name`. */
  summary?: (context: SummaryContext) => string
  /**
   * The formula the node draws on itself ("A + B", "Close-5m > SMA(9)"), its
   * inputs as its own rows show them: a pill, or a letter while unwired.
   */
  equation?: (context: SummaryContext) => string
  /**
   * Rows grouped under titled sections instead of the usual Inputs and
   * Parameters blocks: each section holds its own sockets and fields, in the
   * order given (the Trade Manager's Trigger, Entry, Target, Stop, Risk,
   * Advanced). Every input and every shown (not `hidden`) parameter belongs to
   * exactly one section.
   */
  sections?: FaceSection[]
}

/** One titled group of rows on a sectioned node face (`NodeFace.sections`). */
export interface FaceSection {
  title: string
  /** Input ids, drawn first, in this order. */
  inputs?: string[]
  /** Parameter names, drawn after the inputs, in this order. */
  params?: string[]
  /**
   * Paired rows, drawn after the inputs and parameters: each row is a label
   * and two equal fields side by side, with the row's own sockets right under
   * it (the Trade Manager's Trade settings).
   */
  rows?: FaceRow[]
  /** Toggles among `params` drawn with the settings they turn on (FaceToggleGroup). */
  groups?: FaceToggleGroup[]
  /**
   * Closed until clicked open (Advanced). A section whose socket is wired
   * stays open, so a wire never points at a hidden handle.
   */
  collapsible?: boolean
  /**
   * A quiet block: the title as a small sentence-case label over its rows,
   * no divider under it (a Condition's Left and Right). An empty title draws
   * no label at all.
   */
  plain?: boolean
}

/**
 * One paired row of a sectioned face: a label, then two fields of the same
 * width, then the row's sockets underneath while they are in play.
 */
export interface FaceRow {
  label: string
  /**
   * The first field, then the candidates for the second: the first candidate
   * the node's settings show (its `dependsOn` holds) takes the second slot,
   * and with none of them shown the slot stays empty (Target: Type, then
   * Value for PnL % and Ticks, Price Offset for a price level, nothing for
   * Custom).
   */
  params: string[]
  /** Input ids drawn under the row, without their names, while shown. */
  inputs?: string[]
  /**
   * Per field, by position: 'narrow' (56px) for a field whose values are a
   * symbol or two (an operator), else the paired 96px.
   */
  widths?: Array<'narrow' | 'standard'>
  /**
   * 'columns': the fields side by side, each named above its control (Session
   * Metric's Metric), a 'narrow' one taking the smaller share of the row.
   */
  layout?: 'columns'
  /** With 'columns', each field's name above it, where it differs from the setting's own. */
  labels?: string[]
  /** A word between the two fields, read as one range ("09:30 to 16:00"). */
  joiner?: string
}

/**
 * A toggle and the settings it turns on, drawn as one feature in the toggle's
 * place among its section's `params` (the Trade Manager's Entry Window and
 * Trailing Stop). While it is on, the toggle and its rows share a faint
 * panel; the rows sit under the toggle, right-aligned and a size smaller,
 * label-less unless a field needs its name, and a row's socket keeps its
 * handle level with the toggle, as Target's is with "Target" (Chris,
 * 2026-10-07: "it looks like there's a hierarchy").
 */
export interface FaceToggleGroup {
  /** The on/off setting: one of the section's `params`. */
  toggle: string
  /**
   * Its settings, each drawn while its first field is shown (they depend on
   * the toggle). Only the first row may carry sockets.
   */
  rows: FaceRow[]
}

// Input definition - describes what inputs a node accepts
export interface InputDefinition {
  id: string // e.g. "input_1", "price_data", "signal_a"
  displayName: string // e.g. "Input A", "Price Data"
  // Note: required field removed - automatically determined by inputHandler.type

  // Type constraints - what types of data this input accepts
  acceptedTypes: string[] // e.g. ["number", "ohlcv", "boolean"]
  acceptedSources: string[] // e.g. ["priceData", "sma", "ema"]

  // Strategy node filtering - restrict which node types can connect
  nodeTypeFilter?: string[] // e.g. ["target", "stop"] - only these node types can connect

  // Discriminated union support for Math nodes
  discriminatedUnion?: {
    staticType: 'number' | 'text' | 'boolean'
    defaultStaticValue: string | number | boolean
    parameterName: string // e.g. "inputBType"
    /**
     * The input follows its wire instead of a Node / Static choice (the
     * Condition's B, Chris 2026-10-28): wired it reads the wire, unwired it is
     * a number box beside the same handle, and an empty box is still required.
     * `parameterName` stays stored (hidden) and is kept in line with the wire
     * and the box (utils/static-mode.ts followWire).
     */
    followsWire?: boolean
  }

  /**
   * Conditional requiredness: the input is REQUIRED (unconnected → validator
   * warning) only while the named select parameter's current value (falling
   * back to the parameter's schema default) is in `is`; for any other value
   * the input is optional and may dangle. Absent → always required (status
   * quo, per inputHandler.type). Example: target.signal is load-bearing only
   * for 'Static Value' / 'Condition' target types — Ticks and %-Trade
   * compute off the entry price and ignore the signal.
   */
  requiredWhen?: {
    parameter: string
    is: string[]
  }

  /**
   * In play only while the named parameter (falling back to its schema
   * default) is one of `is`: shown on the node face and required by both
   * validators then, hidden and optional otherwise. A wired input is shown
   * regardless, so a wire never points at a missing socket. Unlike
   * `requiredWhen`, which keeps the socket on screen and only changes whether
   * it must be wired. Read through schemas/input-activity.ts.
   */
  activeWhen?: {
    parameter: string
    is: Array<string | number | boolean>
  }

  /**
   * More settings `activeWhen` needs at once, each read the same way (the
   * Trade Manager's trailing exit condition: Trailing Stop on, and its type
   * Condition). Like a parameter's `dependsOnAll`.
   */
  activeWhenAll?: Array<{
    parameter: string
    is: Array<string | number | boolean>
  }>

  /**
   * A socket no longer offered, and why (where the same thing is set now). It
   * is never drawn unwired and a new wire into it is refused, but a saved wire
   * keeps working and keeps it drawn (the Trade Manager's Window, retired for
   * its From / Until times: Chris, 2026-10-07).
   */
  retired?: string

  /** This input's right-hand control, where it differs from the face's `inputControl`. */
  control?: NodeFace['inputControl']

  /**
   * The face draws this socket's pill without its name, because the row above
   * names it (the Trade Manager's Trigger under its heading, a Custom target's
   * socket under the Target row). Messages and docs still use `displayName`.
   */
  hideLabel?: boolean

  /**
   * The name the face draws beside this socket, where it differs from
   * `displayName`, which messages and docs keep. Drawn without a colon, in a label column
   * wide enough that the rows' controls line up.
   */
  faceLabel?: string

  /**
   * What an unwired socket asks for, in the merged source control ("Connect a
   * limit price"). Without it the control reads "Connect a source".
   */
  emptyText?: string

  /**
   * While unwired, the socket offers to build what it reads, under this label
   * (the Trade Manager's Trigger: "Build entry", an Entry subnet edited as a
   * form). The editor supplies the action.
   */
  build?: string

  /**
   * Never required: wiring it is a choice and nothing is missing without it
   * (the entry window socket, which the node's own fields stand in for).
   * Without this a single-handler node would count every input as required.
   */
  optional?: boolean

  // Field selection for OHLCV data
  fieldSelection?: {
    dynamic?: boolean // If true, get options from connected node's outputs
    options?: Array<{ value: string; label: string }> // Static options
    fallbackOptions?: Array<{ value: string; label: string }> // Fallback for dynamic when no connection
    defaultField: string
  }
}

// Output definition - describes what this node produces
export interface OutputDefinition {
  id: string // e.g. "result", "signal", "upper_band"
  displayName: string // e.g. "Result", "Signal", "Upper Band"
  type: 'number' | 'boolean' | 'ohlcv' | 'array'
  description?: string
  color?: 'green' | 'red' | 'orange' | 'blue' // Handle color for specific outputs
  /** When true, output type mirrors the connected input type (e.g. Bar Offset passes through boolean or number) */
  passthrough?: boolean
  /**
   * The name the face draws beside this output, where it differs from
   * `displayName`, which the node's stored outputs and the engine payload
   * keep (the Condition's True drawn as Signal, like the Crossover's).
   */
  faceLabel?: string
  /**
   * An output no longer offered, and what to do instead. It stays in the
   * schema, so the engine still computes it and a saved wire from it keeps
   * working; the face draws it only while such a wire holds it, and the AI
   * catalog leaves it out (the Condition's False, retired for flipping the
   * operator: Chris, 2026-10-28). Like an input's `retired`.
   */
  retired?: string
}

// Parameter definition - describes configurable parameters (discriminated by `type`)
interface BaseParameterDefinition {
  name: string
  displayName: string
  description?: string
  dependsOn?: {
    parameter: string
    values: Array<string | number | boolean>
  }
  dependsOnAll?: Array<{ parameter: string; values: Array<string | number | boolean> }>
  /**
   * Persisted state the node face must not render. Some parameters exist to be
   * stored and read by code, not edited in a 200px node body: a serialized
   * filter tree, an id pointing at a saved record, the timestamp of the last
   * refresh. They still serialize and still round-trip; they simply have no
   * field. Distinct from `dependsOn`, which hides a parameter the user MAY edit
   * when it is not currently relevant.
   */
  hidden?: boolean
  /**
   * An input id. While that input has a wire, the parameter renders no field,
   * because the wire supplies what the field would have (the entry window's
   * From / Until give way to a wired Window). The value still persists, so
   * unplugging the wire brings the field back as it was.
   */
  hiddenWhenConnected?: string
  /**
   * On a paired row, a value of 0 reads as an inline text button under this
   * label ("Add offset") instead of a field holding a mystery 0; clicking it
   * shows the field. Back at 0, it is the button again.
   */
  addLabel?: string
  /**
   * 'segmented': a select of two or three short options drawn on the node as
   * a toggle, every option showing (Chris, 2026-10-06: the Condition's Node vs
   * Static). Cards and the inspector keep their dropdown.
   */
  display?: 'segmented'
}

export type SelectParameterDefinition = BaseParameterDefinition & {
  type: 'select'
  default: string | number | boolean
  /**
   * `icon` draws beside the label in the menu and the closed field (a
   * Condition's Node / Static); `description` is the option's tooltip there (a
   * Debug Chart's Show on).
   */
  options: Array<{
    value: string | number | boolean
    label: string
    icon?: LucideIcon
    description?: string
  }>
}

export type IntParameterDefinition = BaseParameterDefinition & {
  type: 'int'
  default: number
  min?: number
  max?: number
  step?: number
}

export type FloatParameterDefinition = BaseParameterDefinition & {
  type: 'float'
  default: number
  min?: number
  max?: number
  step?: number
}

export type TextParameterDefinition = BaseParameterDefinition & {
  type: 'text'
  default: string
  /**
   * `ticker`: the value is a US equity symbol. Every editor renders the app's
   * `TickerSearch` (directory autocomplete, upper-cased and trimmed on commit)
   * instead of a free-text box, so a lower-case or padded symbol can never
   * reach the engine, which keys on the exact string.
   */
  format?: 'ticker'
}

export type BooleanParameterDefinition = BaseParameterDefinition & {
  type: 'boolean'
  default: boolean
}

export type TimeParameterDefinition = BaseParameterDefinition & {
  type: 'time'
  default: string
  native?: boolean
  timePresets?: Array<{ label: string; value: string }>
}

export type DateParameterDefinition = BaseParameterDefinition & {
  type: 'date'
  default: string
}

export type ParameterDefinition =
  | SelectParameterDefinition
  | IntParameterDefinition
  | FloatParameterDefinition
  | TextParameterDefinition
  | BooleanParameterDefinition
  | TimeParameterDefinition
  | DateParameterDefinition

// Validation result
export interface ValidationResult {
  isValid: boolean
  errors: string[]
  warnings?: string[]
}

// Custom UI sections for special nodes
export interface CustomSection {
  type: 'equation' | 'chart' | 'custom'
  position:
    | 'before-inputs'
    | 'after-inputs'
    | 'before-parameters'
    | 'after-parameters'
    | 'before-outputs'
    | 'after-outputs'
  render: (data: Record<string, unknown>) => React.ReactNode
}

// Registry for all node schemas - Single source of truth
/** Offered where nodes are added: not deprecated, and not retired from adding (`addable: false`). */
export function isAddableSchema(schema: UniversalNodeSchema): boolean {
  return !schema.deprecated && schema.addable !== false
}

export class UniversalNodeRegistry {
  private static schemas: Map<string, UniversalNodeSchema> = new Map()

  static register(schema: UniversalNodeSchema) {
    this.schemas.set(schema.type, schema)
    // console.log(`Registered schema for node type: ${schema.type}`);
  }

  static getSchema(nodeType: string): UniversalNodeSchema | undefined {
    return this.schemas.get(nodeType)
  }

  static getAllSchemas(): UniversalNodeSchema[] {
    return Array.from(this.schemas.values())
  }

  static getSchemasByCategory(category: string): UniversalNodeSchema[] {
    return Array.from(this.schemas.values()).filter((schema) => schema.category === category)
  }

  static getRegisteredCount(): number {
    return this.schemas.size
  }

  static hasSchema(nodeType: string): boolean {
    return this.schemas.has(nodeType)
  }

  static getRegisteredTypes(): string[] {
    return Array.from(this.schemas.keys())
  }

  /** Type alias map for renamed node types: { oldType: newType } */
  private static aliases: Map<string, string> = new Map()

  /** Register a type alias so old serialized nodes resolve to the new schema. */
  static registerAlias(oldType: string, newType: string): void {
    this.aliases.set(oldType, newType)
  }

  /** Resolve a node type string through the alias map. */
  static resolveType(nodeType: string): string {
    return this.aliases.get(nodeType) ?? nodeType
  }

  /** Lookup that follows aliases. Use this on deserialization paths. */
  static getSchemaResolved(nodeType: string): UniversalNodeSchema | undefined {
    return this.schemas.get(this.resolveType(nodeType))
  }

  /**
   * Validate a single parameter assignment against this node type's own
   * schema. Returns `null` when valid, otherwise a human-readable error.
   *
   * Checks: the parameter name exists in the schema's `parameters`, the value
   * matches the definition's type (`int` rejects non-integers, `select`
   * requires one of the declared options), and int/float `min`/`max` bounds.
   *
   * Opt-in by design: only programmatic callers (dev graph API, recipes)
   * invoke it — the interactive render/change path is untouched, so there is
   * no per-keystroke cost. The check itself is a map lookup plus a linear
   * scan of the node's parameter definitions.
   */
  static validateParameter(nodeType: string, name: string, value: unknown): string | null {
    const schema = this.getSchemaResolved(nodeType)
    if (!schema) return `unknown node type '${nodeType}'`
    const def = schema.parameters.find((p) => p.name === name)
    if (!def) {
      const valid = schema.parameters.map((p) => p.name).join(', ') || '(none)'
      return `'${name}' is not a parameter of '${nodeType}' (valid: ${valid})`
    }
    switch (def.type) {
      case 'int':
        if (typeof value !== 'number' || !Number.isInteger(value)) {
          return `'${name}' expects an integer, got ${JSON.stringify(value)}`
        }
        break
      case 'float':
        if (typeof value !== 'number' || Number.isNaN(value)) {
          return `'${name}' expects a number, got ${JSON.stringify(value)}`
        }
        break
      case 'boolean':
        if (typeof value !== 'boolean') {
          return `'${name}' expects a boolean, got ${JSON.stringify(value)}`
        }
        break
      case 'text':
      case 'time':
      case 'date':
        if (typeof value !== 'string') {
          return `'${name}' expects a string, got ${JSON.stringify(value)}`
        }
        break
      case 'select':
        if (!def.options.some((option) => option.value === value)) {
          const allowed = def.options.map((option) => JSON.stringify(option.value)).join(', ')
          return `'${name}' must be one of ${allowed}, got ${JSON.stringify(value)}`
        }
        break
    }
    if ((def.type === 'int' || def.type === 'float') && typeof value === 'number') {
      const bounds = def as { min?: number; max?: number }
      if (bounds.min !== undefined && value < bounds.min) {
        return `'${name}' must be >= ${bounds.min}, got ${value}`
      }
      if (bounds.max !== undefined && value > bounds.max) {
        return `'${name}' must be <= ${bounds.max}, got ${value}`
      }
    }
    return null
  }
}

// Helper function to create discriminated union inputs (for Math nodes)
export function createDiscriminatedUnionInput(
  id: string,
  displayName: string,
  staticType: 'number' | 'text' | 'boolean' = 'number',
  defaultStaticValue: string | number | boolean = 0,
  acceptedSources: string[] = ['priceData', 'sma', 'ema', 'rsi', 'macd'],
): InputDefinition {
  return {
    id,
    displayName,
    acceptedTypes: [staticType, 'number'],
    acceptedSources,
    discriminatedUnion: {
      staticType,
      defaultStaticValue,
      parameterName: `${id}Type`,
    },
    fieldSelection: {
      options: [
        { value: 'open', label: 'Open' },
        { value: 'high', label: 'High' },
        { value: 'low', label: 'Low' },
        { value: 'close', label: 'Close' },
        { value: 'volume', label: 'Volume' },
      ],
      defaultField: 'close',
    },
  }
}

// Helper function to create standard OHLCV input
export function createOHLCVInput(opts?: { withFieldSelection?: boolean }): InputDefinition {
  const base: InputDefinition = {
    id: 'input_1',
    displayName: 'Data',
    acceptedTypes: ['ohlcv'],
    acceptedSources: ['priceData'],
  }
  if (opts?.withFieldSelection) {
    base.fieldSelection = {
      options: [
        { value: 'open', label: 'Open' },
        { value: 'high', label: 'High' },
        { value: 'low', label: 'Low' },
        { value: 'close', label: 'Close' },
        { value: 'volume', label: 'Volume' },
      ],
      defaultField: 'close',
    }
  }
  return base
}

// Helper function to create standard numeric output
export function createNumericOutput(
  id: string,
  displayName: string,
  description?: string,
): OutputDefinition {
  return {
    id,
    displayName,
    type: 'number',
    description,
  }
}

// Helper function to create standard boolean output
export function createBooleanOutput(
  id: string = 'signal',
  displayName: string = 'Signal',
  description?: string,
): OutputDefinition {
  return {
    id,
    displayName,
    type: 'boolean',
    description,
  }
}

// Helper function to create standard length parameter
export function createLengthParameter(
  defaultValue: number = 20,
  min: number = 1,
  max: number = 1000,
): ParameterDefinition {
  return {
    name: 'length',
    displayName: 'Length',
    type: 'int',
    default: defaultValue,
    min,
    max,
  }
}

// Helper function to register preset wrapper nodes
export function registerPresetNode(
  baseSchema: UniversalNodeSchema,
  presetId: string,
  presetName: string,
  presetDescription: string,
  presetParameters: Record<string, string | number | boolean>,
  presetIcon: LucideIcon,
) {
  // Create a new schema that extends the base schema
  // Uses compound type for registry (to avoid conflicts) but nodes will use base type + presetId
  const presetSchema: UniversalNodeSchema = {
    ...baseSchema,
    type: `${baseSchema.type}:${presetId}`, // e.g., 'timeSlice:premarket-high' for registry
    displayName: presetName,
    description: presetDescription,
    category: 'utilities',
    icon: presetIcon,
    presetId: presetId, // Store preset ID in schema
    baseType: baseSchema.type, // Store base type (e.g., 'timeSlice')
    presetParameters,
    // Hide preset-configured parameters, only show non-preset parameters
    parameters: baseSchema.parameters.filter((param) => !(param.name in presetParameters)),
  }

  UniversalNodeRegistry.register(presetSchema)
}
